import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Charts.css';
import SectionDescription from '../shared/SectionDescription';
import HerdByBreed from './herd/HerdByBreed.js';
import HerdByCauseOfDeath from './herd/HerdByCauseOfDeath.js';
import AgeCategoryChart from './herd/AgeCategoryChart.js';
import ReproductionRecentResults from './reproduction/ReproductionRecentResults.js';
import ReproductionSuccessByMonthAndGroup from './reproduction/ReproductionSuccessByMonthAndGroup.js';
import BullSuccessRate from './reproduction/BullSuccessRate.js';
import ProtocolSuccessRate from './reproduction/ProtocolSuccessRate.js';
import RainfallByMonth from './climate/RainfallByMonth.js';
import TemperatureChart from './climate/TemperatureChart.js';
import WeightChart from './weight/WeightChart.js';

// `size` only controls the tile's width in the flow layout below — never
// touches the chart component itself. Roughly: sm = single donut/short bar,
// md = default, lg = multi-series line or anything with its own filter UI
// (WeightChart) that wants breathing room.
const CATEGORIES = [
  {
    key: 'births',
    charts: [
      { Component: HerdByBreed,          size: 'md' },
      { Component: HerdByCauseOfDeath,   size: 'md' },
      { Component: AgeCategoryChart,     size: 'sm' },
    ],
  },
  {
    key: 'reproduction',
    charts: [
      { Component: ReproductionRecentResults,          size: 'md' },
      { Component: ReproductionSuccessByMonthAndGroup, size: 'lg' },
      { Component: BullSuccessRate,                    size: 'md' },
      { Component: ProtocolSuccessRate,                size: 'md' },
    ],
  },
  {
    key: 'climate',
    charts: [
      { Component: RainfallByMonth,  size: 'lg' },
      { Component: TemperatureChart, size: 'lg' },
    ],
  },
  {
    key: 'weight',
    charts: [
      { Component: WeightChart, size: 'lg' },
    ],
  },
];

const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3H3v6" />
    <path d="M15 3h6v6" />
    <path d="M3 15v6h6" />
    <path d="M21 15v6h-6" />
  </svg>
);

export const Charts = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null); // { Component, categoryKey, i } | null

  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setExpanded(null); };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [expanded]);

  return (
    <div className="Charts">
      <SectionDescription title={t("charts_section_title")} text={t("charts_section_text")} />
      {CATEGORIES.map(category => (
        <div key={category.key} className="chart-category">
          <div className="chart-category-header">
            <span className="chart-category-label">{t(category.key)}</span>
          </div>
          <div className="chart-category-body">
            {category.charts.map(({ Component, size }, i) => (
              <div
                key={i}
                className={`chart-section chart-section--${size}`}
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <button
                  type="button"
                  className="chart-expand-btn"
                  onClick={() => setExpanded({ Component })}
                  aria-label={t('expand_chart')}
                  title={t('expand_chart')}
                >
                  <ExpandIcon />
                </button>
                <Component />
              </div>
            ))}
          </div>
        </div>
      ))}

      {expanded && (
        <>
          <div className="chart-modal-backdrop" onClick={() => setExpanded(null)} />
          <div className="chart-modal" role="dialog" aria-modal="true">
            <button
              type="button"
              className="chart-modal-close"
              onClick={() => setExpanded(null)}
              aria-label={t('close')}
            >
              ✕
            </button>
            <div className="chart-modal-body">
              <expanded.Component />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Charts;
